import { ROLES } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Users module's contracts.
 *
 * Types come from `z.infer`, never hand-written, so the schema and its type
 * cannot drift. Every limit is the one the service enforces, so an over-long
 * name is a sentence beside the input rather than a round trip and a 422.
 */

const NAME_MAX = 200;
const EMAIL_MAX = 254;
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

/** One row in the user list. */
export const userRow = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
  roles: z.array(z.string()),
  status: z.string(),
});

export type UserRow = z.infer<typeof userRow>;

/** A user as the detail endpoint returns it. */
export const userDetail = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  roles: z.array(z.string()),
  status: z.string(),
  propertyIds: z.array(z.string()),
});

export type UserDetail = z.infer<typeof userDetail>;

/** The create endpoint's data payload. */
export const userItem = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
  roles: z.array(z.string()),
  status: z.string(),
});

export type UserItem = z.infer<typeof userItem>;

/** A role option as the list endpoint returns it. */
export const roleOption = z.object({
  id: z.string(),
  name: z.string(),
  isSystem: z.boolean().optional(),
});

export type RoleOption = z.infer<typeof roleOption>;

const SYSTEM_ROLE_NAMES = new Set<string>(Object.values(ROLES));

export function isSystemRole(role: RoleOption): boolean {
  return role.isSystem ?? SYSTEM_ROLE_NAMES.has(role.name);
}

/** A property option from the property picker. */
export const propertyOption = z.object({
  value: z.string(),
  label: z.string(),
});

export type PropertyOption = z.infer<typeof propertyOption>;

/** What the create and edit forms collect. */
export const userFormInput = z.object({
  email: z.string().max(EMAIL_MAX).optional(),
  password: z.string().min(PASSWORD_MIN).max(PASSWORD_MAX).optional(),
  firstName: z.string().max(NAME_MAX).or(z.literal('')).optional(),
  lastName: z.string().max(NAME_MAX).or(z.literal('')).optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED']).or(z.literal('')).optional(),
  roleIds: z.array(z.string()).max(1, 'Only one role can be assigned to a user.'),
  propertyIds: z.array(z.string()),
});

export type UserFormInput = z.infer<typeof userFormInput>;
